import React, { useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { ButtonStyled, TextFieldStyled, TextStyled } from '@nike/nike-design-system-components';
import { Formik } from 'formik';
import { object, string } from 'yup';
import { Link } from 'react-router-dom';
import { Box } from 'rebass';

import { Spinner } from '../components/';
import { TranslatedCopy } from '../intl/TranslatedCopy';

// TODO: translate validation errors and error summaries in bodega
export const validationSchema = object().shape({
  username: string().required('Please enter a username'),
  password: string().required('Please enter a password')
});

const LoginForm = () => {
  const { oktaAuth } = useOktaAuth();
  const [oktaErrorSummary, setOktaErrorSummary] = useState('');
  const [oktaErrorCode, setOktaErrorCode] = useState('');
  const [sessionToken, setSessionToken] = useState();
  const [showSpinner, setShowSpinner] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showFaq, setShowFaq] = useState(false);

  const submit = async values => {
    const { username, password } = values;

    setShowSpinner(true);

    try {
      const res = await oktaAuth.signInWithCredentials({ username, password });
      const { sessionToken } = res;
      setSessionToken(sessionToken);

      // sessionToken is a one-use token, so make sure this is only called once
      await oktaAuth.signInWithRedirect({ sessionToken });
    } catch (err) {
      setOktaErrorSummary(err.errorSummary);
      setOktaErrorCode(err.errorCode);
    } finally {
      setShowSpinner(false);
    }
  };

  const showHelpSigningInLinks = () => {
    setShowForgotPassword(!showForgotPassword);
    setShowFaq(!showFaq);
  };

  if (sessionToken || showSpinner) {
    return (
      <div className='mt4-sm'>
        <Spinner small />
      </div>
    );
  }

  return (
    <Box mt='75px'>
      {oktaErrorSummary && (
        <TextStyled appearance='body2' color='error' style={{ textAlign: 'center', marginBottom: '20px' }}>
          {oktaErrorCode === 'E0000004' ? <TranslatedCopy id='okta.errors.E0000004' /> : oktaErrorSummary}
        </TextStyled>
      )}

      <Formik initialValues={{ username: '', password: '' }} validationSchema={validationSchema} onSubmit={submit}>
        {({ values, touched, handleChange, handleSubmit, errors }) => (
          <form onSubmit={handleSubmit}>
            <TextFieldStyled
              name='username'
              label={new TranslatedCopy({ id: 'global.form.email' }).toString()}
              value={values.username}
              onChange={handleChange}
              error={touched.name && errors.name}
              errorMessage={errors.name}
              className='mb4-sm'
            />

            <TextFieldStyled
              name='password'
              label={new TranslatedCopy({ id: 'profile.credentials.password.title' }).toString()}
              type='password'
              value={values.password}
              onChange={handleChange}
              error={touched.password && errors.password}
              errorMessage={errors.password}
            />

            <ButtonStyled
              id='login-submit'
              type='submit'
              appearance='primary'
              size='medium'
              style={{ margin: '0 auto' }}
            >
              Sign In
            </ButtonStyled>
          </form>
        )}
      </Formik>

      <ButtonStyled
        appearance='textUnderline'
        background='light'
        size='small'
        ariaLabel={new TranslatedCopy({ id: 'okta.needhelp' }).toString()}
        onClick={showHelpSigningInLinks}
        className='mauto-sm mt4-sm'
      >
        <TranslatedCopy id='okta.needhelp' />
      </ButtonStyled>

      {showForgotPassword && (
        <Link to='password/recover'>
          <ButtonStyled
            appearance='textUnderline'
            background='light'
            size='small'
            ariaLabel={new TranslatedCopy({ id: 'okta.forgotpassword' }).toString()}
            onClick={showHelpSigningInLinks}
            className='mauto-sm mt4-sm'
          >
            <TranslatedCopy id='okta.forgotpassword' />
          </ButtonStyled>
        </Link>
      )}
    </Box>
  );
};

export default LoginForm;
